/**
 * useBookDraft.js
 * 
 * Custom hook for book draft CRUD operations.
 * Handles loading, saving, and syncing with Firestore.
 * 
 * Features:
 * - Initial draft loading
 * - Auto-save with debouncing (handled in parent)
 * - Entry data fetching with caching
 * - Optimistic updates
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  doc, 
  getDoc, 
  updateDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../../../firebase'; // Adjust path as needed

export default function useBookDraft(draftId, babyBookId) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache for entry data
  const entryCache = useRef({});
  
  // Load draft from Firestore
  useEffect(() => {
    if (!draftId) {
      setLoading(false);
      return;
    }
    
    const loadDraft = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const draftRef = doc(db, 'bookDrafts', draftId);
        const draftSnap = await getDoc(draftRef);
        
        if (!draftSnap.exists()) {
          throw new Error('Book draft not found');
        }
        
        const draftData = {
          id: draftSnap.id,
          ...draftSnap.data()
        };
        
        setDraft(draftData);
        
        // Pre-fetch entry data for all pages
        const allEntryIds = draftData.pages
          .flatMap(page => page.entries || [])
          .filter((id, index, arr) => arr.indexOf(id) === index); // Unique IDs
        
        if (allEntryIds.length > 0) {
          await fetchEntryData(allEntryIds);
        }
        
      } catch (err) {
        console.error('Error loading draft:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadDraft();
  }, [draftId]);
  
  // Save draft pages to Firestore
  const saveDraft = useCallback(async (pages) => {
    if (!draftId) {
      throw new Error('No draft ID');
    }
    
    try {
      const draftRef = doc(db, 'bookDrafts', draftId);
      
      await updateDoc(draftRef, {
        pages,
        pageCount: pages.length,
        updatedAt: serverTimestamp()
      });
      
      // Update local state
      setDraft(prev => ({
        ...prev,
        pages,
        pageCount: pages.length
      }));
      
      return true;
    } catch (err) {
      console.error('Error saving draft:', err);
      throw err;
    }
  }, [draftId]);
  
  // Fetch entry data (thumbnails, text, etc.)
  const fetchEntryData = useCallback(async (entryIds) => {
    if (!entryIds || entryIds.length === 0) return {};
    
    // Filter out already cached entries
    const uncachedIds = entryIds.filter(id => !entryCache.current[id]);
    
    if (uncachedIds.length === 0) {
      return entryCache.current;
    }
    
    try {
      // Firestore 'in' queries are limited to 30 items
      const batches = [];
      for (let i = 0; i < uncachedIds.length; i += 30) {
        batches.push(uncachedIds.slice(i, i + 30));
      }
      
      const results = {};
      
      for (const batch of batches) {
        const entriesRef = collection(db, 'entries');
        const q = query(
          entriesRef,
          where('__name__', 'in', batch)
        );
        
        const snapshot = await getDocs(q);
        
        snapshot.forEach(doc => {
          const data = doc.data();
          results[doc.id] = {
            id: doc.id,
            thumbnailUrl: getThumbnailUrl(data),
            timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
            text: data.text?.substring(0, 200) || data.notes?.substring(0, 200) || '',
            photoCount: (data.mediaUrls?.length || 0) + (data.photos?.length || 0),
            isMilestone: !!data.isMilestone || !!data.milestoneId,
            milestoneType: data.milestoneType || null
          };
        });
      }
      
      // Update cache
      entryCache.current = {
        ...entryCache.current,
        ...results
      };
      
      return entryCache.current;
      
    } catch (err) {
      console.error('Error fetching entry data:', err);
      return entryCache.current;
    }
  }, []);
  
  // Get all entries for the baby book (for entry picker)
  const fetchAllEntries = useCallback(async (options = {}) => {
    if (!babyBookId) return [];
    
    try {
      const entriesRef = collection(db, 'entries');
      let q = query(
        entriesRef,
        where('babyBookId', '==', babyBookId)
      );
      
      const snapshot = await getDocs(q);
      
      const entries = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        entries.push({
          id: doc.id,
          thumbnailUrl: getThumbnailUrl(data),
          timestamp: data.timestamp?.toDate ? data.timestamp.toDate() : new Date(data.timestamp),
          text: data.text?.substring(0, 200) || data.notes?.substring(0, 200) || '',
          photoCount: (data.mediaUrls?.length || 0) + (data.photos?.length || 0),
          isMilestone: !!data.isMilestone || !!data.milestoneId,
        });
        
        // Also add to cache
        entryCache.current[doc.id] = entries[entries.length - 1];
      });
      
      // Sort by timestamp
      entries.sort((a, b) => b.timestamp - a.timestamp);
      
      return entries;
      
    } catch (err) {
      console.error('Error fetching all entries:', err);
      return [];
    }
  }, [babyBookId]);
  
  // Get cached entry
  const getCachedEntry = useCallback((entryId) => {
    return entryCache.current[entryId] || null;
  }, []);
  
  return {
    draft,
    loading,
    error,
    saveDraft,
    fetchEntryData,
    fetchAllEntries,
    getCachedEntry,
    entryCache: entryCache.current
  };
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the best thumbnail URL from an entry
 * Prefers thumbnail URLs, falls back to full-size
 */
function getThumbnailUrl(entry) {
  // Check for explicit thumbnail
  if (entry.thumbnailUrl) {
    return entry.thumbnailUrl;
  }
  
  // Check mediaUrls array
  if (entry.mediaUrls && entry.mediaUrls.length > 0) {
    const firstMedia = entry.mediaUrls[0];
    // If it's an object with thumbnailUrl
    if (typeof firstMedia === 'object' && firstMedia.thumbnailUrl) {
      return firstMedia.thumbnailUrl;
    }
    // If it's an object with url
    if (typeof firstMedia === 'object' && firstMedia.url) {
      return firstMedia.url;
    }
    // If it's a string
    if (typeof firstMedia === 'string') {
      return firstMedia;
    }
  }
  
  // Check photos array (legacy)
  if (entry.photos && entry.photos.length > 0) {
    const firstPhoto = entry.photos[0];
    if (typeof firstPhoto === 'object' && firstPhoto.url) {
      return firstPhoto.url;
    }
    if (typeof firstPhoto === 'string') {
      return firstPhoto;
    }
  }
  
  // No image found
  return null;
}
